import {
  JATOBasicCard,
  JATOButton,
  JATOButtonGroup,
  JATOGroup,
  JATOSelect,
  JATOSelect2,
  JATOText,
  JATOTheme,
} from '@jato/ui-component-library'
import {
  ICurrencyInfo,
  ISpecsUserSettings,
} from 'models/Specifications/SpecsUserSettings'
import {
  getCurrencies,
  getSpecsUserState,
} from 'redux/specifications/specificationsSlice'

import { CheckboxWithGtm } from 'components/Gtm/CheckboxWithGtm'
import { GtmWrapper } from 'components/Gtm/GtmWrapper'
import { useUpdateUserSettings } from 'hooks/specsUserSettings'
import { useResetBuiltVehiclesAndFilters } from 'hooks/carSpecs'
import React, { useState } from 'react'
import { useAppSelector, useAppDispatch } from 'redux/hook'
import axios from 'axios'
import { store } from 'redux/store'
import { newCarSpecsBaseAPIUrl } from 'config'
import { carSpecsApi } from 'services/apiWithAuth'
import { getTranslations } from 'redux/translations/translationsSlice'
import specificationsService from 'services/Specifications/SpecificationsService'
import {
  setSpecsUserState,
  setCurrencyLanguageInfoState,
} from 'redux/specifications/specificationsSlice'

interface ISpecsSettings {
  onClose: () => void
}

export const SpecsSettings: React.FC<ISpecsSettings> = (params) => {
  const translations = useAppSelector(getTranslations)
  const specsUserState = useAppSelector(getSpecsUserState)
  const currencies = useAppSelector(getCurrencies)
  const dispatch = useAppDispatch()
  const { mutateAsync: updateUserSettings } = useUpdateUserSettings()
  const { mutateAsync: resetBuiltVehiclesAndFilters } =
    useResetBuiltVehiclesAndFilters()

  const priceOptions: string[] = [
    translations.JNT_BasePrice,
    translations.JNT_Country_Specific_Price,
    translations.JNT_RetailPrice,
    translations.JNT_RetailDeliveryPrice,
  ]

  const priceDropdownOptions = priceOptions.map((value) => ({ value }))

  const currencyDropdownOptions = currencies?.map(
    (currency: ICurrencyInfo) => ({
      ...currency,
      value: currency.description,
    })
  )

  const getSelectedCurrency = currencies?.find(
    (data: any) => data.currencyCode === specsUserState.settings.currencyCode
  )

  const getSelectedPrice = (): string | undefined => {
    if (specsUserState.settings.vehiclePriceSettings.isBasePrice) {
      return translations.JNT_BasePrice
    } else if (
      specsUserState.settings.vehiclePriceSettings.isCountrySpecificPrice
    ) {
      return translations.JNT_Country_Specific_Price
    } else if (specsUserState.settings.vehiclePriceSettings.isRetailPrice) {
      return translations.JNT_RetailPrice
    } else if (
      specsUserState.settings.vehiclePriceSettings.isRetailPriceinclDelivery
    ) {
      return translations.JNT_RetailDeliveryPrice
    }
  }

  const [displayLocalVehicleNames, setDisplayLocalVehicleNames] = useState(
    specsUserState.settings.isLocalOptions
  )

  const [excludeEmptyPrice, setExcludeEmptyPrice] = useState(
    specsUserState.settings.excludeEmptyPrice
  )

  const [outgoingModelYears, setOutgoingModelYears] = useState(
    specsUserState.settings.outgoingModelYears
  )

  const [selectedCurrency, setSelectedCurrency] = useState(getSelectedCurrency)

  const [selectedPrice, setSelectedPrice] = useState(getSelectedPrice())

  const handleSubmitSettings = async (): Promise<void> => {
    const updatedSettings: ISpecsUserSettings = {
      ...specsUserState.settings,
      isLocalOptions: displayLocalVehicleNames,
      vehiclePriceSettings: {
        isRetailPrice: selectedPrice == translations.JNT_RetailPrice,
        isBasePrice: selectedPrice == translations.JNT_BasePrice,
        isCountrySpecificPrice:
          selectedPrice == translations.JNT_Country_Specific_Price,
        isCountrySpecificPrice2: false,
        isRetailPriceinclDelivery:
          selectedPrice == translations.JNT_RetailDeliveryPrice,
      },
      excludeEmptyPrice: excludeEmptyPrice,
      outgoingModelYears: outgoingModelYears,
      currencyCode:
        selectedCurrency?.currencyCode ?? specsUserState.settings.currencyCode,
    }

    await updateUserSettings(updatedSettings)
    await resetBuiltVehiclesAndFilters()
    try {
      const { carSpecsUserGuid, v5ContractId } =
        store.getState().specifications.userState

      const url = `${newCarSpecsBaseAPIUrl}/cleanup/reset`
      const payload = { userGuid: carSpecsUserGuid }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      if (carSpecsUserGuid) headers.Authorization = `Basic ${carSpecsUserGuid}`
      if (v5ContractId) headers.JatonetContractId = v5ContractId

      // Second try: use existing carSpecsApi (has interceptors configured)
      try {
        await carSpecsApi.post('/cleanup/reset', payload)
      } catch (apiErr) {
        // swallow/reset error - handled silently
      }
    } catch (error) {
      // reset endpoint error handled silently
    }

    // Refresh licence/user info so server-side session reflects new settings (currency)
    try {
      const getLicenceInfo = {
        languageId: specsUserState.languageId,
        isJatoNet: true,
        guid: specsUserState.guid,
        isJatoSpecs: true,
      }

      const data = await specificationsService.getLicenceInfo(getLicenceInfo)
      if (data?.isSuccess) {
        if (data.user) {
          const defaultMarket =
            data.user?.specsDbMarkets && data.user?.specsDbMarkets[0]?.key

          const lastSelectedMarketIsValid =
            data.user.settings?.lastSelectedMarket &&
            data.user.specsDbMarkets?.some((m) => m.key === data.user.settings.lastSelectedMarket)

          const userStateUpdate = lastSelectedMarketIsValid
            ? data.user
            : {
                ...data.user,
                settings: {
                  ...data.user.settings,
                  lastSelectedMarket: defaultMarket,
                },
              }

          dispatch(setSpecsUserState(userStateUpdate))
        }

        if (data.currencyLanguageInfo)
          dispatch(setCurrencyLanguageInfoState(data.currencyLanguageInfo))
      }
    } catch (e) {
      // ignore licence refresh errors
    }

    params.onClose()
  }

  const handleSelectedCurrency = (value: string): void => {
    const selectedCurrency = currencies?.find(
      (data: any) => data.description === value
    )
    setSelectedCurrency(selectedCurrency)
  }

  const handleSelectedPrice = (value: string): void => {
    const selectedPriceOption = priceOptions.find((data: any) => data === value)
    setSelectedPrice(selectedPriceOption)
  }

  return (
    <JATOBasicCard height="calc(100vh - 160px)">
      <JATOGroup flexDirection="column">
        <JATOGroup aria-label="Brands" role="group">
          <GtmWrapper id="specsSettingsCurrencySelect">
            <JATOSelect2
              label={translations.JNT_currency}
              width="100%"
              id="currencyDropdown"
              dataSrc={currencyDropdownOptions ?? []}
              value={selectedCurrency?.description}
              onChange={handleSelectedCurrency}
              hideClearSelection={true}
              ignoreCase={true}
              displayOnlyUnselectedOptions={true}
            />
          </GtmWrapper>
        </JATOGroup>
        <JATOGroup aria-label="Brands" role="group">
          <GtmWrapper id="specsSettingsCurrencySelect">
            <JATOSelect
              label={translations.JNT_price}
              width="100%"
              id="priceDropdown"
              dataSrc={
                priceDropdownOptions.filter((value) => value !== undefined) ??
                []
              }
              value={selectedPrice?.toString()}
              onChange={handleSelectedPrice}
            />
          </GtmWrapper>
        </JATOGroup>
        <JATOGroup flexDirection="column">
          <JATOGroup
            aria-label="Brands"
            role="group"
            style={{ marginBottom: '20px' }}
          >
            <CheckboxWithGtm
              id="specsSettingsDisplayLocalVehicleNamesCheckbox"
              checked={displayLocalVehicleNames}
              onChange={() =>
                setDisplayLocalVehicleNames(!displayLocalVehicleNames)
              }
              label={translations.JNT_local}
            >
              <JATOText marginRight={JATOTheme.space[6]} as="p" fontSize={14}>
                {translations.JNT_local}
              </JATOText>
            </CheckboxWithGtm>
          </JATOGroup>
          <JATOGroup
            aria-label="Brands"
            role="group"
            style={{ marginBottom: '20px' }}
          >
            <CheckboxWithGtm
              id="specsSettingsHideVehiclesWithoutPriceCheckbox"
              checked={excludeEmptyPrice}
              onChange={() => setExcludeEmptyPrice(!excludeEmptyPrice)}
              label={translations.JNT_WPrice}
            >
              <JATOText marginRight={JATOTheme.space[3]} as="p" fontSize={14}>
                {translations.JNT_WPrice}
              </JATOText>
            </CheckboxWithGtm>
          </JATOGroup>
          <JATOGroup
            aria-label="Brands"
            role="group"
            style={{ marginBottom: '20px' }}
          >
            <CheckboxWithGtm
              id="specsSettingsOutgoingModelYearCheckbox"
              checked={outgoingModelYears}
              onChange={() => setOutgoingModelYears(!outgoingModelYears)}
              label={translations.JNT_excludeold}
            >
              <JATOText marginRight={JATOTheme.space[3]} as="p" fontSize={14}>
                {translations.JNT_excludeold}
              </JATOText>
            </CheckboxWithGtm>
          </JATOGroup>
        </JATOGroup>
        <JATOButtonGroup justifyContent="flex-end">
          <JATOButton
            id="specsSettingsSubmitButton"
            textTransform="uppercase"
            onClick={() => handleSubmitSettings()}
            variant="primary"
          >
            {translations.JNT_Submit}
          </JATOButton>
        </JATOButtonGroup>
      </JATOGroup>
    </JATOBasicCard>
  )
}
